import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

// 全局拦截器保留
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 || error.response?.data?.message === '认证失败') {
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo'); // 新增：清除用户信息缓存
      delete axios.defaults.headers.common['Authorization'];
      if (window.authNavigate) window.authNavigate('/login', { replace: true });
      console.log('认证失败，请重新登录');
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // 挂载导航方法到window
  useEffect(() => {
    window.authNavigate = navigate;
    return () => delete window.authNavigate;
  }, [navigate]);

  // 初始化：优先从本地缓存读取用户信息，避免重复请求接口
  useEffect(() => {
    const init = async () => {
      try {
        // 步骤1：读取本地token
        const token = localStorage.getItem('token');
        if (!token) {
          setLoading(false);
          return;
        }

        // 步骤2：先读取本地缓存的用户信息（避免刷新重复请求接口）
        const cachedUser = localStorage.getItem('userInfo');
        if (cachedUser) {
          const userInfo = JSON.parse(cachedUser);
          setUser(userInfo); // 直接恢复用户信息
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          setLoading(false);
          return;
        }

        // 步骤3：本地无缓存，调用接口获取用户信息
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const res = await axios.get('/api/user/profile');
        if (res.data) {
          const userInfo = res.data.data || res.data;
          setUser(userInfo);
          localStorage.setItem('userInfo', JSON.stringify(userInfo)); // 缓存用户信息到本地
        }
      } catch (err) {
        console.error('初始化失败:', err);
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // 登录：同时缓存token和用户信息
  const login = async (username, password) => {
    try {
      const res = await axios.post('/api/login', { username, password });
      const { token, user } = res.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('userInfo', JSON.stringify(user)); // 缓存用户信息
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      
      return { success: true, user };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || '登录失败' };
    }
  };

  // 登出：清除所有缓存
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    navigate('/login', { replace: true });
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isLogin: !!user }}>
      {!loading && children} {/* 加载完成前不渲染任何内容 */}
    </AuthContext.Provider>
  );
};
